import React, { useMemo, useState, useEffect, useRef } from 'react';
// ColumnInstance might not be explicitly needed if Column is sufficient for typing
import { useTable, useSortBy, usePagination, Column } from 'react-table'; 

// Types
import { InstanceDisplayRow, ClientTagColumnDefinition } from './Tags'; // Import from Tags.tsx
// import TagsType from '../../../types/Tags'; // Not directly used here, InstanceDisplayRow has _originalTags

// Components
import ReactTable from '../../../component/ReactTable';
import Alert from '../../../component/Alert';
import { getDefaultPageSize } from '../../../helpers/utils';
import SearchInput from '../../../component/SearchInput';

// Styles
import './tags.scss';

// Helper for editable cell - editable dropdown (combobox)
const EditableCell = ({
  value: initialValue,
  row: { original },
  column: { id },
  onCellEdit,
  getUniqueTagValuesForColumn,
  isAdmin,
}: { value: any, row: { original: InstanceDisplayRow }, column: { id: string }, onCellEdit: Function, getUniqueTagValuesForColumn: Function, isAdmin: boolean }) => {
  const [value, setValue] = useState(initialValue);
  const [isOpen, setIsOpen] = useState(false);
  const uniqueValues = useMemo(() => getUniqueTagValuesForColumn(id), [getUniqueTagValuesForColumn, id]);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const onBlur = () => {
    // Delay to allow dropdown click to register
    setTimeout(() => {
      if (initialValue !== value) {
        onCellEdit(original.instanceId, id, value === '' ? null : value);
      }
      setIsOpen(false);
    }, 150);
  };

  const handleSelect = (selectedValue: string) => {
    setValue(selectedValue);
    onCellEdit(original.instanceId, id, selectedValue);
    setIsOpen(false);
  };

  const handleClear = () => {
    setValue('');
    onCellEdit(original.instanceId, id, null);
    setIsOpen(false);
  };

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  if (!isAdmin) {
    return (
      <span>
        {value || <span className="text-muted">-</span>}
      </span>
    );
  }

  return (
    <div ref={containerRef} className="editable-cell position-relative">
      <div className="input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="form-control form-control-sm"
          value={value || ''}
          onChange={onChange}
          onFocus={() => setIsOpen(true)}
          onBlur={onBlur}
          data-lpignore={true}
        />
        {value ? (
          <button
            type="button"
            className="clear-btn"
            onMouseDown={(e) => { e.preventDefault(); handleClear(); }}
            tabIndex={-1}
            title="Clear"
          >
            <i className="fal fa-times"></i>
          </button>
        ) : (
          <button
            type="button"
            className="dropdown-toggle"
            onClick={() => {
              setIsOpen(!isOpen);
              if (!isOpen) inputRef.current?.focus();
            }}
            tabIndex={-1}
          >
            <span className="visually-hidden">Toggle Dropdown</span>
          </button>
        )}
      </div>
      {isOpen && uniqueValues.length > 0 && (
        <ul 
          className="dropdown-menu show w-100" 
          style={{ maxHeight: '150px', overflowY: 'auto' }}
        >
          {uniqueValues.map((val: string, index: number) => (
            <li key={index}>
              <button
                type="button"
                className={`dropdown-item ${val === value ? 'active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(val); }}
              >
                {val}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

interface TagListProps {
  data: InstanceDisplayRow[];
  columns: ClientTagColumnDefinition[];
  loadingState: boolean;
  onCellEdit: (instanceId: number, tagName: string, newValue: string | null) => void;
  onNewColumn: (newColumnName: string) => void;
  getUniqueTagValuesForColumn: (tagName: string) => string[]; // To populate datalist
  handleSaveChanges: () => Promise<void>; // Added prop for save function
  isSaving: boolean; // Added prop to disable button while saving
  hasUnsavedChanges: boolean; // Added prop to track unsaved changes
  isAdmin: boolean; // Added prop for admin access control
}

const TagList: React.FC<TagListProps> = ({
  data,
  columns: columnDefs, // Renaming to avoid conflict with react-table's columns
  loadingState,
  onCellEdit,
  onNewColumn,
  getUniqueTagValuesForColumn,
  handleSaveChanges, // Destructure new prop
  isSaving, // Destructure new prop
  hasUnsavedChanges, // Destructure new prop
  isAdmin, // Destructure new prop
}) => {
  const [newTagName, setNewTagName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return data;
    }
    const lowerSearch = searchTerm.toLowerCase();
    return data.filter(row => {
      // Search in instance name
      if (row.instanceName.toLowerCase().includes(lowerSearch)) {
        return true;
      }
      // Search in tag values
      for (const colDef of columnDefs) {
        const tagValue = row[colDef.name];
        if (tagValue && String(tagValue).toLowerCase().includes(lowerSearch)) {
          return true;
        }
      }
      return false;
    });
  }, [data, searchTerm, columnDefs]);

  const handleAddNewColumn = () => {
    if (newTagName.trim() && !columnDefs.find(col => col.name === newTagName.trim())) {
      onNewColumn(newTagName.trim());
      setNewTagName('');
      setErrorMessage(null);
    } else {
      setErrorMessage("Tag name already exist.");
    }
  };

  const tableColumns = useMemo<Column<InstanceDisplayRow>[]>(() => {
    const staticColumns: Column<InstanceDisplayRow>[] = [
      {
        Header: 'Instance',
        accessor: 'instanceName',
        // disableSortBy: true, // Removed to enable sorting
        width: 200, // Example width for Instance column
        sortType: (rowA, rowB, columnId) => {
          const a = String(rowA.values[columnId]).toLowerCase();
          const b = String(rowB.values[columnId]).toLowerCase();
          if (a > b) return 1;
          if (b > a) return -1;
          return 0;
        },
      },
    ];

    const dynamicColumns: Column<InstanceDisplayRow>[] = columnDefs.map(colDef => ({
      Header: colDef.name,
      accessor: colDef.name as string, // Accessor must be a string or function
      width: 150, // Example default width for tag columns
      Cell: (cellProps: any) => ( // cellProps needs typing
        <EditableCell
          {...cellProps}
          onCellEdit={onCellEdit}
          getUniqueTagValuesForColumn={getUniqueTagValuesForColumn}
          isAdmin={isAdmin}
        />
      ),
    }));

    return [...staticColumns, ...dynamicColumns];
  }, [columnDefs, onCellEdit, getUniqueTagValuesForColumn]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns: tableColumns,
      data: filteredData,
      initialState: {
        pageIndex: 0,
        pageSize: getDefaultPageSize(),
      },
      autoResetPage: false,
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetSelectedRows: false,
      autoResetExpanded: false,
      autoResetGroupBy: false,
    },
    useSortBy,
    usePagination
  );

  return (
    <div className="card">
      <div className="card-header">
        <i className="fal fa-tags fa-fw" aria-hidden="true" /> Instance Tags
        <span className="badge bg-info ms-2" data-tip="Total instances count">
          {searchTerm ? `${filteredData.length} / ${data.length}` : data.length}
        </span>
        <i 
          className="collapse-toggle" 
          role="button" 
          data-bs-toggle="collapse"
          data-bs-target="#collapseInstanceTags" 
          aria-expanded="false" 
          aria-controls="collapseInstanceTags"
        />
      </div>
      <div id="collapseInstanceTags" className="card-body collapse show">
        {errorMessage && (
          <Alert 
            message={errorMessage}
            heading="Invalid Tag Name" 
            variant="alert-danger"
          />
        )}
        <div className="row row-cols-1 row-cols-md-2 table-search">
          <div className="col col-md-10 d-flex align-items-center">
            {isAdmin && (
              <>
                <input
                  type="text"
                  className="form-control form-control-sm me-2"
                  style={{ width: '200px', height: '31px' }}
                  placeholder="New Tag Name"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') handleAddNewColumn();}}
                  data-lpignore={true}
                />
                <button
                  className='btn btn-sm btn-success me-2'
                  onClick={handleAddNewColumn}
                  disabled={!newTagName}
                  title="Create Tag"
                >
                  <i className="fal fa-plus-square fa-fw"></i><span>Create</span>
                </button>
                <button
                  className='btn btn-sm btn-primary'
                  onClick={handleSaveChanges}
                  disabled={!hasUnsavedChanges || isSaving || loadingState}
                  title="Save all changes"
                >
                  <i className="fal fa-save fa-fw"></i><span>Save Changes</span>
                </button>
              </>
            )}
          </div>
          <div className="col col-md-2 d-flex align-items-center">
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setSearchTerm('')}
            />
          </div>
        </div>
        <ReactTable
          getTableProps={getTableProps}
          getTableBodyProps={getTableBodyProps}
          headerGroups={headerGroups}
          prepareRow={prepareRow}
          page={page}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageOptions={pageOptions}
          pageCount={pageCount}
          gotoPage={gotoPage}
          nextPage={nextPage}
          previousPage={previousPage}
          setPageSize={setPageSize}
          pageIndex={pageIndex}
          pageSize={pageSize}
          dataLength={filteredData.length}
          error={null}
          loading={loadingState}
        />
      </div>
    </div>
  );
};

export default TagList;
